import {
  useLazyGetCaseOverviewQuery,
  useLazyGetCaseQuery,
  useLazyGetLogoutQuery,
  useLazyGetPremexClosedQuery,
  useLazyGetUserEmailsQuery,
  useLazyPostSwitchOrgQuery,
  useLazyGetListOrgsQuery,
} from '@/api';
import type { ListOrg } from '@/api/debug/listOrgs';
import {
  AccessControlAction,
  AppRoutes,
  DEFAULT_VISIBLE_CASE_STATUSES,
  TECHNOLOGY_SUPPORT_EMAIL,
  isProduction,
} from '@/constants';
import { useAccessControl } from '@/hooks';
import { selectCaseMetadata, selectCaseOverview, selectUserAboutData, useAppSelector } from '@/store';
import { LogoIcon, Dropdown } from '@repo/atomic-components';
import type { DropdownChangeEvent } from '@repo/atomic-components';
import { getAvatarColorsFromName } from '@repo/utils';

import { SVGProps, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useMatch, useNavigate, useSearchParams, useLocation } from 'react-router';

/**
 * Derive up-to-two-letter initials from the `userName` returned by
 * `/user/about`. Splits on whitespace and takes the first letter of the first
 * and last segment ("Jane Doe" -> "JD"); a single-token name yields one
 * letter, and an absent / empty name falls back to "?".
 */
const getInitialsFromUserName = (userName: string | undefined): string => {
  if (!userName) return '?';
  const parts = userName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return `${first}${last}`.toUpperCase() || '?';
};

/**
 * Title-case a name field for display. Uppercases the first letter of every
 * word and lowercases the rest, so "scott", "SCOTT", and "Scott" all land at
 * "Scott". Word boundaries cover hyphens and apostrophes, so "smith-jones"
 * becomes "Smith-Jones" and "o'brien" becomes "O'Brien". Mc / Mac prefixes
 * are not specially handled — "mcdonald" lands at "Mcdonald", which is a
 * known trade-off we accept until/unless users flag it.
 */
const capitaliseName = (name: string | null | undefined): string => {
  if (!name) return '';
  return name.toLowerCase().replace(/\b[a-z]/g, (char) => char.toUpperCase());
};

/**
 * Prettify a UserRoleType value for display ("GLOBAL_ADMIN" -> "Global Admin",
 * "MEDICAL_RECORDS_ADMIN" -> "Medical Records Admin").
 */
const formatUserRole = (role: string | undefined): string => {
  if (!role) return '';
  return role
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : ''))
    .join(' ');
};

/**
 * Convert underscore-separated constant names to an all-caps display label.
 * e.g., "LAW_FIRM_A" -> "LAW FIRM A", "FLETCHERS" -> "FLETCHERS"
 */
const convertOrgNameToDisplay = (name: string): string => {
  return name.split('_').join(' ').toUpperCase();
};

// Nav-specific icons. Stroke/fill use `currentColor` so they inherit the
// link's text color (grey by default, dark teal #0D2D29 when active).
const navIconProps: SVGProps<SVGSVGElement> = {
  width: 18,
  height: 18,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

// Scales of justice — vertical pole on a short base, with a crossbeam and two
// triangular pans hanging from the beam ends. Reads as "legal" rather than
// "office", which is more appropriate for the top-level Cases list.
const CasesIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...navIconProps} {...props}>
    {/* Vertical pole */}
    <path d="M12 4v17" />
    {/* Base */}
    <path d="M8 21h8" />
    {/* Crossbeam */}
    <path d="M5 7h14" />
    {/* Left pan: V-chains from the beam end down to a rim, closed as a tri.
        7 units tall (apex y=7 → rim y=14) so the pans read clearly at 18px. */}
    <path d="M5 7l-3 7h6z" />
    {/* Right pan */}
    <path d="M19 7l-3 7h6z" />
  </svg>
);

// Briefcase — rectangle with a small handle arc on top, used alongside the
// current case reference. Previously served as the Cases icon before it was
// promoted onto the case-ref item.
const CaseRefIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...navIconProps} {...props}>
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M3 13h18" />
  </svg>
);

// Stacked documents: a front doc with a folded corner and a second doc
// peeking out behind it — reads as a bundle of records rather than a
// single page or an "add document" action.
const MedicalRecordsIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...navIconProps} {...props}>
    <path d="M15.5 2H8.6c-.4 0-.8.2-1.1.5-.3.3-.5.7-.5 1.1v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8c.4 0 .8-.2 1.1-.5.3-.3.5-.7.5-1.1V6.5L15.5 2z" />
    <path d="M3 7.6v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8" />
    <path d="M15 2v5h5" />
  </svg>
);

const UsersIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...navIconProps} {...props}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Cases and Users pages don't read URL params, so they get the current
  // search string with every Medical-Records-owned filter param stripped
  // out — otherwise navigating away from Medical Records leaks its search
  // term, case-status, provider, etc. into the Cases URL as visual noise.
  // Medical Records gets its own `medicalRecordsSearchParams` below, seeded
  // with the default filter.
  const nonMedicalRecordsSearchParams = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    (
      [
        'caseStatus',
        'checkedBy',
        'documentStatus',
        'providerTypes',
        'providerId',
        'uploadedBy',
        'minUploadDate',
        'maxUploadDate',
        'nameFilter',
      ] as const
    ).forEach((key) => params.delete(key));
    return params.toString();
  }, [searchParams]);

  // Medical Records page nav target — seed the caseStatus filter with the
  // default "everything except Closed / Awaiting CMS Closure" set unless the
  // user already has a caseStatus in the URL (e.g. they're navigating back
  // after tweaking filters). The URL-sync effect on the page turns this into
  // the selected filter state on load, which is the simplest reliable way
  // to seed that filter without racing the RTK Query response.
  const medicalRecordsSearchParams = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    if (!params.has('caseStatus')) {
      params.set('caseStatus', DEFAULT_VISIBLE_CASE_STATUSES.join(','));
    }
    return params.toString();
  }, [searchParams]);
  // Avatar popup is a self-rolled state-driven panel rather than a PrimeReact
  // Menu, because the Menu API is item-based and can't host section headings
  // + grouped action buttons cleanly. We portal it to `document.body` and
  // position it via the button's bounding rect, so no ancestor's
  // `overflow:hidden` or stacking context can clip it.
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const [avatarMenuPos, setAvatarMenuPos] = useState<{ top: number; right: number } | null>(null);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const avatarMenuRef = useRef<HTMLDivElement>(null);

  const toggleAvatarMenu = () => {
    if (isAvatarMenuOpen) {
      setIsAvatarMenuOpen(false);
      return;
    }
    if (avatarButtonRef.current) {
      const rect = avatarButtonRef.current.getBoundingClientRect();
      setAvatarMenuPos({
        top: rect.bottom + 8, // 8px gap below the avatar
        right: window.innerWidth - rect.right, // align popup's right edge with the button
      });
    }
    setIsAvatarMenuOpen(true);
  };

  const isViewUserManagementTab = useAccessControl(AccessControlAction.VIEW_USER_MANAGEMENT_TAB);
  const isGlobalAdminOnly = useAccessControl(AccessControlAction.GLOBAL_ADMIN_ONLY);
  const isviewMedicalRecordsTab = useAccessControl(AccessControlAction.VIEW_MEDICAL_RECORDS_TAB);
  const isViewBundles = useAccessControl(AccessControlAction.VIEW_BUNDLES);

  // The avatar button shows initials derived from `userName` on `/user/about`.
  // App.tsx triggers the query at boot and dispatches the response into the
  // store, so we only need to read it here.
  const userAboutData = useAppSelector(selectUserAboutData);
  const userInitials = getInitialsFromUserName(userAboutData?.userName);
  // Per-user avatar colour — A-Z each get one of 26 evenly distributed hues
  // (A=red, M=green-cyan, T=purple, Z=back near pink). Anything else (digits,
  // punctuation, accented letters, missing names) falls back to the original
  // mauve, so non-personalised avatars look exactly like they used to.
  const avatarColors = getAvatarColorsFromName(userAboutData?.userFirstName, userAboutData?.userName);
  // Check if we're on production environment
  const isProductionEnvironment = isProduction();
  // Check if we're on the GDPR page (/gdpr/) and where we came from
  const isGdprPage = /\/gdpr\//.test(location.pathname);
  const fromParam = searchParams.get('from');

  // Override active state for GDPR page based on 'from' parameter
  const isCasesActive = isGdprPage && fromParam === 'case';
  const isMedicalRecordsActive = isGdprPage && fromParam === 'medical-records';

  // ---------- Contextual case sub-navigation ----------
  // Whenever the URL is anything under `/case-list/:caseId/...` we surface a
  // case-specific sub-nav between [Cases] and [Medical Records]. Driving this
  // off the URL (rather than the Redux `caseId` slice) keeps it working on
  // bundle / timeline / chronology routes, which are siblings of
  // CaseItemLayout and don't write into that slice themselves.
  const caseContextMatch = useMatch(`/${AppRoutes.CaseList}/:caseId/*`);
  const caseIdFromUrl = caseContextMatch?.params.caseId;
  const isInCaseContext = !!caseIdFromUrl;

  // Pre-fetch case + overview when we enter a case. `preferCacheValue` keeps
  // this a no-op when CaseItemLayout already triggered the same queries.
  const [triggerGetCase] = useLazyGetCaseQuery();
  const [triggerGetCaseOverview] = useLazyGetCaseOverviewQuery();
  useEffect(() => {
    if (!caseIdFromUrl) return;
    triggerGetCase({ caseId: caseIdFromUrl }, true);
    triggerGetCaseOverview({ caseId: caseIdFromUrl }, true);
  }, [caseIdFromUrl, triggerGetCase, triggerGetCaseOverview]);

  const caseMetadata = useAppSelector(selectCaseMetadata);
  const caseOverview = useAppSelector(selectCaseOverview);
  // Avoid flashing a stale case ref mid-navigation between two cases.
  const caseReference = caseMetadata && caseMetadata.caseId === caseIdFromUrl ? caseMetadata.caseReference : undefined;

  // Each AppRoutes value below is a unique URL segment used at most once per
  // path, so `pathname.includes('/<segment>/')` is a safe match without
  // needing a full route pattern. Overview = case detail page itself
  // (uploaded-medical-records / ai-processed-documents tabs), i.e. inside
  // the case but on none of the dedicated sub-pages.
  const { pathname } = location;
  const isFullBundlePath = pathname.includes(`/${AppRoutes.FullBundle}/`);
  const isLightBundlePath = pathname.includes(`/${AppRoutes.LightBundle}/`);
  const isTimelinePath = pathname.includes(`/${AppRoutes.Timeline}/`);
  const isChronologyPath = pathname.includes(`/${AppRoutes.Chronology}/`);
  const isOverviewActive =
    isInCaseContext && !isFullBundlePath && !isLightBundlePath && !isTimelinePath && !isChronologyPath;

  // True when the current case has at least one navigable sub-page (a bundle
  // the user can view, a chronology, or a timeline). Drives whether we render
  // the case sub-nav as a framed group (case ref + chevron + children) or
  // collapse it to just the case ref styled like any other top-level item.
  const hasCaseChildren = !!(
    (isViewBundles && (caseOverview?.fullBundle || caseOverview?.lightBundle)) ||
    caseOverview?.chronology ||
    caseOverview?.timeline
  );

  const [triggerGetLogout] = useLazyGetLogoutQuery();
  const [triggerPostSwitchOrg] = useLazyPostSwitchOrgQuery();
  const [triggerGetPremexClosed] = useLazyGetPremexClosedQuery();
  const [triggerGetUserEmails] = useLazyGetUserEmailsQuery();
  const [triggerGetListOrgs] = useLazyGetListOrgsQuery();

  const [orgs, setOrgs] = useState<ListOrg[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<ListOrg | null>(null);

  const navLinkStyles = (isActive: boolean, forceActive: boolean = false) => {
    // Active item is a 32px-tall highlighted rectangle in pale mint with a
    // deep-green label and 16px side padding for the chip silhouette.
    // Inactive items have no horizontal padding so the 24px gap on the
    // parent <nav> is the only spacing between them. Icons inherit the
    // link's text color via `currentColor`.
    const base =
      'flex h-8 items-center gap-2 rounded-sm text-sm font-semibold whitespace-nowrap focus-visible:outline-fletchers-green-500';
    const active =
      isActive || forceActive
        ? 'bg-fletchers-green-50 text-fletchers-green-600 px-4'
        : 'text-fletchers-gray-200 hover:text-fletchers-black-900 px-0';
    return `${base} ${active}`;
  };

  const handleHomeClick = () => {
    navigate('/', { relative: 'path' });
  };

  const handleDownloadPremexClosed = () => {
    triggerGetPremexClosed()
      .unwrap()
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'premex.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Failed to download premex CSV:', error);
      });
  };

  const handleDownloadUserEmails = () => {
    triggerGetUserEmails()
      .unwrap()
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'user-emails.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        console.error('Failed to download user emails:', error);
      });
  };

  const handleOrgChange = (e: DropdownChangeEvent) => {
    const org = e.value as ListOrg;
    // Optimistically reflect the selection, but remember the previous one so we
    // can roll back if the switch fails (otherwise the dropdown would show an
    // org the user isn't actually in).
    const previousOrg = selectedOrg;
    setSelectedOrg(org);
    triggerPostSwitchOrg({ orgId: org.orgId })
      .unwrap()
      .then(() => {
        window.location.reload();
      })
      .catch((error) => {
        setSelectedOrg(previousOrg);
        console.error('Failed to switch org:', error);
      });
  };

  // Fetch orgs when dropdown opens
  useEffect(() => {
    if (isAvatarMenuOpen && isGlobalAdminOnly) {
      triggerGetListOrgs()
        .unwrap()
        .then((response) => {
          const sortedOrgs = [...response.orgs].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
          );
          setOrgs(sortedOrgs);
          // Set the current org as default based on user's organisationId
          if (userAboutData?.organisationId) {
            const currentOrg = sortedOrgs.find((o) => o.orgId === userAboutData.organisationId);
            setSelectedOrg(currentOrg ?? null);
          }
        })
        .catch((error) => console.error('Failed to fetch orgs:', error));
    }
  }, [isAvatarMenuOpen, isGlobalAdminOnly, triggerGetListOrgs, userAboutData?.organisationId]);

  const handleLogout = () => {
    setIsAvatarMenuOpen(false);
    triggerGetLogout({})
      .unwrap()
      .then(() => {
        navigate(`/${AppRoutes.Login}`);
      });
  };

  // Outlook-web "deep link" compose URL to technology support. We use the
  // web compose link rather than mailto: because the Windows default-app
  // prompt lets users accidentally bind mailto: to Chrome/Edge, after which
  // every click opens a blank tab. The deep link goes straight to Outlook
  // on the web in a new tab, which every non-MLS user here is signed into.
  //
  // The body is split into two sections: a free-text placeholder for the
  // user's description, and a diagnostic block at the bottom populated from
  // /user/about (always available — fetched at app boot) plus /case (only
  // when the user is genuinely on a case page, so we don't leak a stale
  // case ref onto, e.g., a Medical Records page report).
  //
  // Memoised so the URL rebuilds only when its inputs change, rather than on
  // every Header render (the menu's open/close toggle would otherwise churn
  // the string each time).
  const supportComposeHref = useMemo(() => {
    const ccEmail = userAboutData?.userEmail ?? '';
    const bodyLines: string[] = ['Please explain your issue below:', '', '', '---', 'Diagnostic info (do not edit):'];
    if (userAboutData) {
      bodyLines.push(
        `User ID: ${userAboutData.userId}`,
        `Username: ${userAboutData.userName}`,
        `Organisation ID: ${userAboutData.organisationId}`,
      );
    }
    // Only include case fields when the loaded metadata matches the case in
    // the URL — guards against showing the previously-viewed case after the
    // user has navigated away.
    const hasMatchingCase = caseMetadata && caseMetadata.caseId === caseIdFromUrl;
    if (hasMatchingCase) {
      bodyLines.push(
        `Case Reference: ${caseMetadata.caseReference}`,
        `Case ID: ${caseMetadata.caseId}`,
        `Status: ${caseMetadata.status}`,
        `Status Updated: ${caseMetadata.statusUpdated}`,
      );
    }
    // Hand-encode rather than using URLSearchParams: the latter form-encodes
    // spaces as `+`, and Outlook's deep-link parser passes those through
    // literally instead of expanding to spaces — the body would render as
    // `Please+explain+your+issue+below:` to the user.
    const query = [
      `to=${encodeURIComponent(TECHNOLOGY_SUPPORT_EMAIL)}`,
      `cc=${encodeURIComponent(ccEmail)}`,
      `subject=${encodeURIComponent('[Mermaidd] I have found an issue')}`,
      `body=${encodeURIComponent(bodyLines.join('\n'))}`,
    ].join('&');
    return `https://outlook.office.com/mail/deeplink/compose?${query}`;
  }, [userAboutData, caseMetadata, caseIdFromUrl]);

  // Close the avatar popup on outside click + Escape + scroll/resize (the
  // popup is positioned from the button's captured rect, so a scroll/resize
  // would otherwise leave it floating in the wrong place). All listeners are
  // mounted only while the popup is open.
  useEffect(() => {
    if (!isAvatarMenuOpen) return;
    const handleClickOutside = (event: globalThis.MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (avatarMenuRef.current?.contains(target) || avatarButtonRef.current?.contains(target)) {
        return;
      }
      // Dropdown panels are portalled to document.body, so check if click is inside one
      if (target instanceof Element && target.closest('.p-dropdown-panel')) {
        return;
      }
      setIsAvatarMenuOpen(false);
    };
    // Scroll/resize close — ignore scrolls that originate inside the popup
    // itself (e.g. a future scrollable section in the menu wouldn't slam it
    // shut mid-interaction).
    const close = (event?: Event) => {
      if (event && event.target instanceof Element) {
        if (avatarMenuRef.current?.contains(event.target)) return;
        if (event.target.closest('.p-dropdown-panel')) return;
      }
      setIsAvatarMenuOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsAvatarMenuOpen(false);
    };
    // Use 'click' instead of 'mousedown' so dropdown onChange fires first
    document.addEventListener('click', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', close);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, [isAvatarMenuOpen]);

  /*
   * Glassy nav bar (mild two-tone glow).
   * - `bg-gradient-to-b from-white/82 to-white/68` is a slightly milky base
   *   so the coloured glows below can bleed through.
   * - Shadow stack (no outer drop shadow — keeps the bar from darkening the
   *   content beneath it):
   *     • inset 0 1px 0 white/75 → specular highlight along the top edge
   *     • 0 1px 0 gray-110/55    → soft hairline divider at the bottom
   * - The aria-hidden coloured layer below paints *inside* the bar:
   *     • Teal (#045A62 family) glow on the LEFT
   *     • Mauve glow on the RIGHT — mirrors the avatar pill's pastel-mauve
   * `isolate` contains the -z-10 layer to this stacking context.
   */
  return (
    <header className="relative isolate flex h-full w-full items-center justify-between bg-gradient-to-b from-white/95 to-white/90 px-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.8),0_1px_0_rgba(225,225,224,0.55)] backdrop-blur-xl backdrop-saturate-[1.4]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            // Teal wash with its core just past the left edge — kept subtle
            // so the bar still reads as mostly white.
            'radial-gradient(26% 130% at -2% 50%, rgba(4,90,98,0.13) 0%, transparent 60%),' +
            // Right-edge wash mirrors the avatar pill's hue (per-user via
            // `getAvatarColorsFromName`) so the bar looks like the pill is the
            // light source. Falls back to the original mauve rgba when the
            // user's first letter isn't A-Z.
            `radial-gradient(22% 130% at 102% 50%, ${avatarColors.washColor} 0%, transparent 65%)`,
        }}
      />
      {/* Left group: logo + nav. `min-w-0` lets it shrink below its intrinsic
          width (default `min-width: auto` on a flex child would otherwise
          refuse to give ground); `overflow-hidden` clips any overflow cleanly
          so it can't slide under the avatar. Paired with `shrink-0` on the
          right group, the avatar is always pinned to the right edge even
          when the case sub-nav is wide. */}
      <div className="flex h-full min-w-0 items-center justify-items-start overflow-hidden">
        <div className="flex items-center">
          <LogoIcon className="cursor-pointer" onClick={handleHomeClick} aria-label="Mermaidd home" />
          <div className="bg-fletchers-gray-110/55 mr-6 ml-[24px] h-4 w-[1px]" />
        </div>
        <nav className="flex h-full items-center gap-6">
          {/* `end` keeps Cases highlighted only at `/case-list` itself; once
              the user drills into a case, the case sub-nav below takes over. */}
          <NavLink
            to={`/${AppRoutes.CaseList}?${nonMedicalRecordsSearchParams}`}
            end
            className={({ isActive }) => navLinkStyles(isActive, isCasesActive)}
          >
            <CasesIcon />
            Cases
          </NavLink>
          {isInCaseContext && caseIdFromUrl && !hasCaseChildren && caseReference && (
            // No sub-pages on this case — render the case ref as a plain
            // top-level item, sharing the outer nav's `gap-6` rhythm with
            // Cases on its left and Medical Records / Users on its right.
            // `max-w-[200px]` + a `truncate` span caps long refs with an
            // ellipsis. `min-w-0` lets the span actually shrink inside the
            // flex parent (otherwise the default `min-width: auto` refuses
            // to clip the text); `shrink-0` on the icon stops the flex
            // squeeze from also shrinking the 18px briefcase.
            <NavLink
              to={`/${AppRoutes.CaseList}/${caseIdFromUrl}`}
              className={`${navLinkStyles(false, isOverviewActive)} max-w-[200px]`}
            >
              <CaseRefIcon className="shrink-0" />
              <span className="min-w-0 truncate">{caseReference}</span>
            </NavLink>
          )}
          {isInCaseContext && caseIdFromUrl && hasCaseChildren && (
            // Case sub-nav group: framed by a light-grey 1px border (same hue
            // as the old vertical dividers) with `rounded-[6px]` corners.
            // Sizing:
            //   • `h-10` (40px) inside the 48px nav leaves a 4px gap above and
            //     below the box; `items-center` centres the 32px active chip
            //     inside it.
            //   • `px-6` (24px) mirrors the outer `gap-6` (24px) — the box's
            //     left edge sits equidistant between Cases and the first inner
            //     item, and the right edge equidistant between the last inner
            //     item and Medical Records.
            //   • Internal `gap-6` keeps the 24px rhythm between siblings.
            <div className="border-fletchers-gray-110/55 flex h-10 items-center gap-6 rounded-[6px] border bg-white px-6">
              {caseReference && (
                // The case ref doubles as the Overview entry point — when
                // we're on the case page itself (no sub-page selected), it
                // takes the highlighted chip style via `isOverviewActive`.
                // `max-w-[200px]` + a `truncate` span caps long refs with
                // an ellipsis; `min-w-0` lets the span actually shrink
                // inside the flex parent; `shrink-0` on the icon stops the
                // flex squeeze from also shrinking the 18px briefcase.
                <NavLink
                  to={`/${AppRoutes.CaseList}/${caseIdFromUrl}`}
                  className={`${navLinkStyles(false, isOverviewActive)} max-w-[200px]`}
                >
                  <CaseRefIcon className="shrink-0" />
                  <span className="min-w-0 truncate">{caseReference}</span>
                </NavLink>
              )}
              {/* Breadcrumb chevron — pointing from the case ref into the
                  sub-page list. The box only renders when at least one
                  sub-page exists (see `hasCaseChildren`), so this is
                  unconditional inside the box. */}
              <svg
                aria-hidden
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-fletchers-gray-150"
              >
                <polyline points="9 6 15 12 9 18" />
              </svg>
              {isViewBundles && caseOverview?.fullBundle && (
                <NavLink
                  to={`/${AppRoutes.CaseList}/${caseIdFromUrl}/${AppRoutes.AiProcessedDocuments}/${AppRoutes.FullBundle}/${caseOverview.fullBundle.bundleId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={navLinkStyles(false, isFullBundlePath)}
                >
                  Full Bundle
                </NavLink>
              )}
              {isViewBundles && caseOverview?.lightBundle && (
                <NavLink
                  to={`/${AppRoutes.CaseList}/${caseIdFromUrl}/${AppRoutes.AiProcessedDocuments}/${AppRoutes.LightBundle}/${caseOverview.lightBundle.bundleId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={navLinkStyles(false, isLightBundlePath)}
                >
                  Light Bundle
                </NavLink>
              )}
              {caseOverview?.chronology && (
                <NavLink
                  to={`/${AppRoutes.CaseList}/${caseIdFromUrl}/${AppRoutes.AiProcessedDocuments}/${AppRoutes.Chronology}/${caseIdFromUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={navLinkStyles(false, isChronologyPath)}
                >
                  Chronology
                </NavLink>
              )}
              {caseOverview?.timeline && (
                <NavLink
                  to={`/${AppRoutes.CaseList}/${caseIdFromUrl}/${AppRoutes.AiProcessedDocuments}/${AppRoutes.Timeline}/${caseIdFromUrl}`}
                  className={navLinkStyles(false, isTimelinePath)}
                >
                  Timeline
                </NavLink>
              )}
            </div>
          )}
          {isviewMedicalRecordsTab && (
            <NavLink
              to={`/${AppRoutes.MedicalRecords}?${medicalRecordsSearchParams}`}
              className={({ isActive }) => navLinkStyles(isActive, isMedicalRecordsActive)}
            >
              <MedicalRecordsIcon />
              Medical Records
            </NavLink>
          )}
          {isViewUserManagementTab && (
            <NavLink
              to={`/${AppRoutes.UserManagement}?${nonMedicalRecordsSearchParams}`}
              className={({ isActive }) => navLinkStyles(isActive, false)}
            >
              <UsersIcon />
              Users
            </NavLink>
          )}
        </nav>
      </div>
      {/* Right group: avatar. `shrink-0` keeps it at its natural width so a
          wide left group can't push it off the viewport. `ml-4` adds a
          minimum gap from any nav content that's clipped right up to the
          left group's edge by `overflow-hidden`. */}
      <div className="ml-4 flex h-full shrink-0 items-center justify-items-end">
        <button
          ref={avatarButtonRef}
          onClick={toggleAvatarMenu}
          aria-controls="avatar-menu-popup"
          aria-haspopup="menu"
          aria-expanded={isAvatarMenuOpen}
          aria-label={userAboutData?.userName ? `Open account menu for ${userAboutData.userName}` : 'Open account menu'}
          // Pill colours come from `getAvatarColorsFromName` — the first letter
          // of the user's first name rotates a single hue around the wheel,
          // with the same saturation/lightness as the original mauve. Missing
          // / non-letter first chars return the original mauve hex pair, so
          // the right-edge radial glow still reads as the pill's source then.
          style={{
            backgroundColor: avatarColors.backgroundColor,
            color: avatarColors.color,
            outlineColor: avatarColors.color,
          }}
          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-sm font-semibold focus-visible:outline-2"
        >
          {/* Nudge initials up 1px — capital glyphs in this font sit slightly
              below the optical centre of the 32px circle without it. */}
          <span className="relative -top-px">{userInitials}</span>
        </button>
        {isAvatarMenuOpen &&
          avatarMenuPos &&
          createPortal(
            <div
              ref={avatarMenuRef}
              id="avatar-menu-popup"
              role="menu"
              // Portalled to <body> so no ancestor's overflow / stacking
              // context can clip the popup. Positioned via the button's
              // captured rect; closes on scroll/resize so it can't drift.
              style={{ position: 'fixed', top: avatarMenuPos.top, right: avatarMenuPos.right, zIndex: 1000 }}
              className="bg-fletchers-white border-fletchers-gray-110 flex w-72 flex-col overflow-visible rounded-lg border py-3 shadow-lg"
            >
              {/* User identity ------------------------------------------------ */}
              <div className="flex flex-col gap-1 px-4">
                <span className="text-fletchers-black-900 text-[18px] leading-6 font-semibold">
                  {userAboutData
                    ? `${capitaliseName(userAboutData.userFirstName)} ${capitaliseName(userAboutData.userLastName)}`.trim() ||
                      userAboutData.userName ||
                      '—'
                    : '—'}
                </span>
                <span className="text-fletchers-gray-200 text-sm">{formatUserRole(userAboutData?.userRole)}</span>
              </div>

              <hr className="border-fletchers-gray-110 my-3" />

              {/* Report an issue ----------------------------------------------
                  Opens an Outlook-on-the-web compose window in a new tab,
                  pre-filled with support on To, the user on CC, and (when
                  on a case page) the case reference in the body. We avoid
                  mailto: because Windows' default-protocol prompt lets users
                  accidentally bind it to Chrome/Edge, after which every
                  click opens a blank tab.

                  Hidden for MLS users (`isMls: true` on /user/about) — they
                  use a different internal channel for tech support, so the
                  Outlook deep-link to `technologysupport@medicallawservices`
                  isn't the right route for them. */}
              {!userAboutData?.isMls && (
                <>
                  <a
                    href={supportComposeHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsAvatarMenuOpen(false)}
                    className="text-fletchers-black-900 flex w-full cursor-pointer items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-gray-100"
                  >
                    <i className="pi pi-flag text-xs" aria-hidden />
                    Report an issue
                  </a>

                  <hr className="border-fletchers-gray-110 my-3" />
                </>
              )}

              {/* Global-admin: dashboard link + organisation switcher -------- */}
              {isGlobalAdminOnly && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAvatarMenuOpen(false);
                      navigate(`/${AppRoutes.GlobalAdminDashboard}`);
                    }}
                    className="text-fletchers-black-900 flex w-full cursor-pointer items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-gray-100"
                  >
                    <i className="pi pi-th-large text-xs" aria-hidden />
                    Global Admin Dashboard
                  </button>

                  <hr className="border-fletchers-gray-110 my-3" />

                  <div className="flex flex-col gap-1 px-4">
                    <span className="text-fletchers-gray-200 mb-2 text-sm font-normal">Organisation</span>
                    <Dropdown
                      value={selectedOrg}
                      options={orgs}
                      optionLabel="name"
                      onChange={handleOrgChange}
                      placeholder="Select organisation"
                      className="w-full"
                      appendTo="self"
                      panelClassName="!w-full !min-w-0 [&_.p-dropdown-item]:!whitespace-normal [&_.p-dropdown-item]:!break-words [&_.p-dropdown-item-label]:!whitespace-normal"
                      itemTemplate={(org: ListOrg) => (
                        <span className="block max-w-full min-w-0 break-words whitespace-normal uppercase">
                          {convertOrgNameToDisplay(org.name)}
                        </span>
                      )}
                      valueTemplate={(org: ListOrg | null) =>
                        org ? (
                          <span className="text-fletchers-black-900 uppercase">
                            {convertOrgNameToDisplay(org.name)}
                          </span>
                        ) : (
                          <span className="text-fletchers-gray-200">Select organisation</span>
                        )
                      }
                    />
                  </div>

                  <hr className="border-fletchers-gray-110 my-3" />

                  {/* Global-admin actions ------------------------------------ */}
                  <div className="flex flex-col gap-1">
                    <span className="text-fletchers-gray-200 mb-1 px-4 text-sm font-normal">Global admin actions</span>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAvatarMenuOpen(false);
                        handleDownloadUserEmails();
                      }}
                      className="text-fletchers-black-900 flex w-full cursor-pointer items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-gray-100"
                    >
                      <i className="pi pi-download text-xs" aria-hidden />
                      Download user list
                    </button>
                    {isProductionEnvironment && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsAvatarMenuOpen(false);
                          handleDownloadPremexClosed();
                        }}
                        className="text-fletchers-black-900 flex w-full cursor-pointer items-center gap-2 px-4 py-1.5 text-left text-sm hover:bg-gray-100"
                      >
                        <i className="pi pi-download text-xs" aria-hidden />
                        Export Premex recently closed
                      </button>
                    )}
                  </div>

                  <hr className="border-fletchers-gray-110 my-3" />
                </>
              )}

              {/* Logout (always) ---------------------------------------------- */}
              <button
                type="button"
                onClick={handleLogout}
                className="hover:bg-fletchers-red-500/10 text-fletchers-red-500 flex w-full cursor-pointer items-center gap-2 px-4 py-1.5 text-left text-sm"
              >
                <i className="pi pi-sign-out text-xs" aria-hidden />
                Log out
              </button>
            </div>,
            document.body,
          )}
      </div>
    </header>
  );
};
